import axios from 'axios';

/**
 * E1 — canonical Sheba API client for the website.
 *
 * Replaces the legacy prototype's Bearer+localStorage client
 * (previously: JWT stored in localStorage, sent via an Authorization
 * header, pointed at the old website-only backend). This client instead
 * follows the exact pattern already proven in frontend/customer/src/api/
 * client.ts (the customer portal) — HttpOnly cookie auth
 * (withCredentials: true), a 401 → /auth/refresh → retry interceptor —
 * because the website is architecturally just another browser client of
 * the SAME canonical backend, not a separate system with its own auth.
 *
 * REACT_APP_API_URL is build-time (Create React App requirement — unlike
 * Vite, there is no runtime env injection here). Defaults to the relative
 * path '/api' so a production build behind the same nginx reverse-proxy
 * convention the admin/customer portals already use works with zero
 * configuration; override only for local development against a
 * differently-hosted backend.
 */
const BASE = process.env.REACT_APP_API_URL || '/api';

export const api = axios.create({
  baseURL: BASE,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

const SKIP_REFRESH = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/mfa/verify', '/auth/logout', '/auth/me'];

let _isRefreshing = false;
let _queue = [];

function _drainQueue(error) {
  _queue.forEach((p) => (error ? p.reject(error) : p.resolve()));
  _queue = [];
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config;
    const status = err.response?.status;
    const url = original?.url ?? '';

    if (status === 401 && !SKIP_REFRESH.some((s) => url.includes(s))) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _queue.push({ resolve, reject });
        }).then(() => api(original));
      }

      _isRefreshing = true;
      try {
        await api.post('/auth/refresh');
        _drainQueue();
        return api(original);
      } catch (refreshErr) {
        _drainQueue(refreshErr);
        window.dispatchEvent(new CustomEvent('sheba:unauthorized'));
        return Promise.reject(refreshErr);
      } finally {
        _isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

export default api;
